import React, { useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';

const Contact = () => {
    const [status, setStatus] = useState({
        submitting: false,
        succeeded: false,
        error: null
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ submitting: true, succeeded: false, error: null });

        const formData = new FormData(e.target);

        // Formspreeのエンドポイント
        // 以下の "YOUR_FORM_ID" を、Formspreeで取得したご自身のForm IDに書き換えてください
        // 例: "https://formspree.io/f/xzyqjklm"
        const FORMSPREE_ENDPOINT = "https://formspree.io/f/xovgqkbb";

        try {
            const response = await fetch(FORMSPREE_ENDPOINT, {
                method: "POST",
                body: formData,
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (response.ok) {
                setStatus({ submitting: false, succeeded: true, error: null });
                e.target.reset();
            } else {
                const data = await response.json();
                const errorMessage = data.errors ? data.errors.map(err => err.message).join(", ") : "送信に失敗しました。";
                setStatus({ submitting: false, succeeded: false, error: errorMessage });
            }
        } catch (error) {
            setStatus({ submitting: false, succeeded: false, error: "ネットワークエラーが発生しました。" });
        }
    };

    return (
        <section id="contact" className="py-20 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
            <div className="container mx-auto px-4 md:px-6">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">お問い合わせ</h2>
                    <p className="text-gray-600 dark:text-gray-300">
                        内容がまだ固まっていない段階でも、お気軽にご相談ください。
                    </p>
                </div>

                <div className="max-w-2xl mx-auto">
                    {status.succeeded ? (
                        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl p-8 text-center">
                            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">送信完了</h3>
                            <p className="text-gray-600 dark:text-gray-300">
                                お問い合わせありがとうございます。<br />
                                担当者より順次ご連絡させていただきます。
                            </p>
                            <button
                                onClick={() => setStatus({ ...status, succeeded: false })}
                                className="mt-6 text-green-600 dark:text-green-400 font-medium hover:underline"
                            >
                                他のお問い合わせを送る
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/*
                              スパムボット対策のハニーポット。人間には見えないので空のままだが、
                              フォームを機械的に埋めるボットは値を入れてしまう。_gotcha は
                              Formspree の予約フィールド名で、埋まっている送信はダッシュボードにも
                              残さず破棄される（＝無料プランの送信数も消費しない）。
                            */}
                            <input
                                type="text"
                                name="_gotcha"
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                style={{ display: 'none' }}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        お名前
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        required
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-transparent outline-none transition-colors"
                                        placeholder="お名前を入力してください"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        メールアドレス
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        required
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-transparent outline-none transition-colors"
                                        placeholder="example@email.com"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    件名
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    required
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-transparent outline-none transition-colors"
                                    placeholder="件名を入力してください"
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    お問い合わせ内容
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    required
                                    rows={6}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-transparent outline-none transition-colors resize-none"
                                    placeholder="お問い合わせ内容を入力してください"
                                ></textarea>
                            </div>

                            {status.error && (
                                <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm">
                                    {status.error}
                                </div>
                            )}

                            <div className="text-center">
                                <button
                                    type="submit"
                                    disabled={status.submitting}
                                    className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-200 text-white dark:text-gray-900 font-bold rounded-full transition-all shadow-lg hover:shadow-gray-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {status.submitting ? (
                                        <>
                                            <Loader2 className="animate-spin" size={18} />
                                            送信中...
                                        </>
                                    ) : (
                                        <>
                                            送信する
                                            <Send size={18} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Contact;
